import cx from 'classnames'
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { EditorView } from 'prosemirror-view'
import { EditorState, Selection, Transaction } from 'prosemirror-state'
import { Node, Slice } from 'prosemirror-model'
import { keymap } from 'prosemirror-keymap'
import { baseKeymap } from 'prosemirror-commands'
import { history } from 'prosemirror-history'
import { parseHTMLStringToFragment, serializeFragmentToHTMLString } from 'proseShared/utils'
import { schema } from './Schema'
import { buildKeymap } from './Keymap'
import { HeadingView } from './nodeViews/HeadingView'
import { OutlineEditorHandle, OutlineEditorProps } from './Types'
import Styles from './OutlineEditor.module.scss'

const createState = (config: { doc: Node; selection?: Selection }) => {
  return EditorState.create({
    ...config,
    plugins: [keymap(buildKeymap()), keymap(baseKeymap), history()],
  })
}

const OutlineEditor = forwardRef<OutlineEditorHandle, OutlineEditorProps>(
  ({ className, initialHtmlString = '', onChange, ...rest }, fRef) => {
    const ref = useRef<HTMLDivElement>(null)
    const viewRef = useRef<EditorView | null>(null)

    useImperativeHandle(fRef, () => ({
      view: () => {
        return viewRef.current
      },
      focus: () => {
        viewRef.current?.focus()
      },
      blur: () => {
        ref.current?.blur()
      },
      clear: () => {
        if (!viewRef.current) return
        const { state } = viewRef.current
        const tx = state.tr.replace(0, state.doc.content.size, Slice.empty)
        viewRef.current.dispatch(tx)
      },
      replaceHTMLString: (htmlString: string) => {
        if (!viewRef.current) return
        const doc = schema.nodes.doc.create({}, parseHTMLStringToFragment(htmlString, schema))
        const newState = createState({ doc, selection: Selection.atEnd(doc) })
        viewRef.current.updateState(newState)
        onChange?.(serializeFragmentToHTMLString(newState.doc.content, schema))
      },
    }))

    useEffect(() => {
      if (!ref.current) return
      const state = createState({
        doc: schema.nodes.doc.create({}, parseHTMLStringToFragment(initialHtmlString || '<h2></h2>', schema)),
      })
      const editorView = new EditorView(ref.current, {
        attributes: { 'aria-label': rest['aria-label'] || '' },
        state,
        nodeViews: {
          heading(node: Node, view: EditorView, getPos: () => number) {
            return new HeadingView(node, view, getPos)
          },
        },
        dispatchTransaction(tr: Transaction) {
          let newState = this.state.apply(tr)
          if (newState.doc.content.size === 0) {
            const resetTr = newState.tr
            resetTr.insert(0, schema.nodes.heading.create({ level: 2 }))
            resetTr.setSelection(Selection.atStart(resetTr.doc))
            newState = newState.apply(resetTr)
          }
          this.updateState(newState)
          onChange?.(serializeFragmentToHTMLString(newState.doc.content, schema))
        },
      })

      viewRef.current = editorView

      return () => editorView.destroy()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return <div className={cx(Styles.outlineEditor, 'prose-outline', className)} ref={ref} />
  },
)

export { OutlineEditor }
